import type { ChangeEvent, MouseEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import Switch from '@mui/material/Switch';
import type { DiscountType } from './types';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { TextField, Typography, SegmentedButtonGroup, SegmentedButton } from '@proposify/ui';
import { BlueLemonade, Navy } from '@proposify/tokens/semantic-tokens/index';
import { State } from '@proposify/tokens/semantic-tokens/activeState';
import { fontSize } from '@proposify/tokens/semantic-tokens/fontSize';
import { discountSectionTestIds } from './DiscountSection.test-ids';

// Semantic aliases: palette tokens are solid hex; alpha is composed as a hex
// suffix (de=87%, 61=38%, 14=8%) until the app theme is based on designToken.
const TEXT_PRIMARY = `${Navy['500']}de`;
const TEXT_DISABLED = `${Navy['500']}61`;
const SELECTED_FILL = `${BlueLemonade['500']}14`;

interface DiscountSectionProps {
  enabled: boolean;
  type: DiscountType;
  amount: string;
  errorKey: string | null;
  currencySymbol: string;
  onToggle: () => void;
  onAmountChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onTypeChange: (event: MouseEvent<HTMLElement>, newType: DiscountType) => void;
  inputRef?: Ref<HTMLInputElement>;
}

export const PanelSectionStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const SectionHeaderStyled = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'nowrap',
}));

export const AmountRowStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
}));

export const PanelFieldStyled = styled(TextField)(() => ({
  '& .MuiOutlinedInput-input': {
    padding: '8px 0',
  },
  '& .MuiOutlinedInput-root': {
    padding: '0 12px',
  },
}));

export const SubtotalFieldStyled = styled(PanelFieldStyled)(() => ({
  '& .MuiInputAdornment-positionEnd': {
    '& .MuiTypography-root': {
      color: TEXT_PRIMARY,
    },
  },
  '& .MuiInputAdornment-positionStart': {
    marginRight: 8,
  },
}));

export const AmountFieldStyled = styled(PanelFieldStyled)(() => ({
  flex: 1,
  // The % / $ identifier: string adornments render as InputAdornment > Typography
  '& .MuiInputAdornment-positionEnd .MuiTypography-root': {
    color: TEXT_PRIMARY,
    fontSize: fontSize['5'],
    lineHeight: 1.5,
  },
  // Dim the identifier together with the input when the discount toggle is off
  '& .MuiInputBase-root.Mui-disabled .MuiInputAdornment-positionEnd .MuiTypography-root': {
    color: TEXT_DISABLED,
  },
}));

export const TypeToggleGroupStyled = styled(SegmentedButtonGroup)(() => ({
  height: 40,
  '& .MuiToggleButton-root': {
    padding: '10px 15px',
    fontWeight: 600,
    color: TEXT_PRIMARY,
    borderColor: State.outlinedBorder,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    backgroundColor: SELECTED_FILL,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  '&& .MuiToggleButtonGroup-grouped:first-of-type': {
    borderRadius: '8px 0 0 8px',
  },
  '&& .MuiToggleButtonGroup-grouped:last-of-type': {
    borderRadius: '0 8px 8px 0',
  },
})) as typeof SegmentedButtonGroup;

export const DiscountSection = ({
  enabled,
  type,
  amount,
  errorKey,
  currencySymbol,
  onToggle,
  onAmountChange,
  onTypeChange,
  inputRef,
}: DiscountSectionProps) => {
  const { t } = useTranslation();
  const amountIdentifier = type === 'percentage' ? '%' : currencySymbol;

  return (
    <PanelSectionStyled>
      <SectionHeaderStyled>
        <Typography fontWeight={600} fontSize={fontSize['3']}>
          {t('pricing.discount.label')}
        </Typography>
        <Switch
          data-testid={discountSectionTestIds.toggle}
          size="small"
          inputProps={{ 'aria-label': t('pricing.discount.label') }}
          checked={enabled}
          onChange={onToggle}
        />
      </SectionHeaderStyled>

      <AmountRowStyled>
        <AmountFieldStyled
          data-testid={discountSectionTestIds.amount}
          label={t('pricing.discount.amount')}
          value={amount}
          placeholder=""
          disabled={!enabled}
          error={errorKey !== null}
          helperText={errorKey ? t(errorKey) : null}
          size="small"
          onChange={onAmountChange}
          endAdornment={amountIdentifier}
          inputRef={inputRef}
        />

        <TypeToggleGroupStyled aria-label={t('pricing.discount.type_label')} value={type} onChange={onTypeChange} size="small">
          <SegmentedButton value="percentage" data-testid={discountSectionTestIds.typePercentage} disabled={!enabled}>
            {t('pricing.discount.type_percentage')}
          </SegmentedButton>
          <SegmentedButton value="fixed" data-testid={discountSectionTestIds.typeFixed} disabled={!enabled}>
            {currencySymbol}
          </SegmentedButton>
        </TypeToggleGroupStyled>
      </AmountRowStyled>
    </PanelSectionStyled>
  );
};
